import { useScrollLock } from '@vueuse/core'

/**
 * This composable is used to lock the scroll of the document.
 *
 * A component can lock and unlock the scroll of the document, but it takes
 * multiple instances into account, so that the document will only unlock when
 * all instances are unlocked.
 */
export default function useDocumentScrollLock() {
  /**
   * The set of active components that have locked the document.
   */
  const activeComponents = useState<Set<symbol>>('document-scroll-lock', () => new Set())

  /**
   * The unique identifier for the current component.
   */
  const id = Symbol('lock')

  /**
   * Whether the document is locked.
   */
  const isLocked = useScrollLock(document)

  /**
   * Attempts to lock the document.
   */
  function lock() {
    activeComponents.value.add(id)
  }

  /**
   * Attempts to unlock the document.
   */
  function unlock() {
    activeComponents.value.delete(id)
  }

  /**
   * Watch for changes in active components and update scroll lock accordingly.
   */
  watchEffect(() => {
    isLocked.value = activeComponents.value.size > 0
  })

  /**
   * Always unlock the document when the component is unmounted.
   */
  onBeforeUnmount(() => {
    unlock()
  })

  return {
    lock,
    unlock,
    isDocumentLocked: readonly(isLocked),
  }
}
